import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormArray,
  FormControl,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { VendedorService } from '../../../shared/services/apis/pessoa/vendedor.service';
import { VendedorRequest } from '../../../shared/services/requests/pessoa/vendedor.request';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { VendedorResponse } from '../../../shared/services/responses/pessoa/vendedor.response';
import { environment } from '../../../../environments/environment';
import { PessoaLookupService } from '../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AutocompletePesquisavelComponent } from '../../../shared/components/autocomplete-pesquisavel/autocomplete-pesquisavel.component';
import { SelectPesquisavelService } from '../../../shared/components/select-pesquisavel/select-pesquisavel.service';
import { TipoPessoaEnum } from '../../../shared/enums/pessoa/tipo-pessoa.enum';
import { TipoComissaoEnum } from '../../../shared/enums/pessoa/tipo-comissao.enum';
import { SituacaoVendedorEnum } from '../../../shared/enums/pessoa/situacao-vendedor.enum';
import { CEPCorreios, UtilService } from '../../../shared/services/util.service';
import { ConsultaCnpjService } from '../../../shared/services/apis/pessoa/consulta-cnpj.service';
import { ConsultaCnpjResponse } from '../../../shared/services/responses/pessoa/consulta-cnpj.response';

import {
  PageScrollSpyComponent,
  ScrollSpySection,
} from '../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { UsuarioService } from '../../../shared/services/apis/usuario/usuario.service';
import { VendedorComissaoFaixaStruct } from '../../../shared/services/structs/pessoa/vendedor-comissao-faixa.struct';
import { Masks, MaskService } from '../../../shared/services/mask.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { cpfValidator } from '../../../shared/validators/cpf.validator';
import { cnpjValidator } from '../../../shared/validators/cnpj.validator';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { MunicipioService } from '../../../shared/services/apis/usuario/municipio.service';
import { UnidadeFederativaService } from '../../../shared/services/apis/usuario/unidade-federativa.service';
import { MotorFiscalLookupService } from '../../../shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { VendedorHierarquiaStruct } from '../../../shared/services/structs/pessoa/vendedor-hierarquia.struct';
import { VendedorHierarquiaHelper } from '../../../shared/helpers/vendedor-hierarquia.helper';
import { EscopoVisualizacaoEnum } from '../../../shared/enums/pessoa/escopo-visualizacao.enum';
import { VendedorEquipeSeletorComponent } from './components/vendedor-equipe-seletor/vendedor-equipe-seletor.component';
import { AlertCardComponent } from '../../../shared/components/alert-card/alert-card.component';
import { PessoaConfiguracaoCampoObrigatorioService } from '@shared/services/apis/pessoa/pessoa-configuracao-campo-obrigatorio.service';
import { CampoObrigatorioStruct } from '@shared/services/structs/financas/campo-obrigatorio.struct';
import { TelaPessoaEnum } from '@shared/enums/pessoa/tela-pessoa.enum';
import { valorMaiorQueZeroValidator } from '@shared/validators/valor-maior-que-zero.validator';
import { minArrayLengthValidator } from '@shared/validators/min-array-length.validator';

@Component({
  selector: 'app-vendedor-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatRadioModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    LoadingBoxComponent,
    EmptyStateComponent,
    SelectPesquisavelComponent,
    AutocompletePesquisavelComponent,
    PageScrollSpyComponent,
    NgxMaskDirective,
    FooterActionComponent,
    HeaderComponent,
    NgxEditorModule,
    NgxCurrencyDirective,
    VendedorEquipeSeletorComponent,
    AlertCardComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './vendedor-cadastro.component.html',
  styleUrl: './vendedor-cadastro.component.css',
})
export class VendedorCadastroComponent implements OnInit, OnDestroy {
  public TipoComissaoEnum = TipoComissaoEnum;
  public moduloEnum = ModuloEnum.Cadastros;
  public funcionalidadeEnum = FuncionalidadeEnum.Cadastros_Vendedores;

  public form: FormGroup;
  public isEditMode: boolean = false;
  public isLoading: boolean = false;
  public idVendedor: number | null = null;
  public isSaving: boolean = false;
  public isLoadingCep: boolean = false;
  public isLoadingCnpj: boolean = false;
  public masks: Masks = MaskService.getMasks();
  public percentMask: any;
  public editor!: Editor;
  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  // URLs for Online Selects
  public get urlMunicipio(): string {
    return `${environment.urlApiUsuario}Municipio/select?idUnidadeFederativa=${this.form?.get('idUnidadeFederativa')?.value ?? 0}`;
  }

  public urlVendedor = `${environment.urlApiPessoa}Lookup/listVendedorSelect`;

  // Offline Lists (Lookups)
  public listaPaises: any[] = [];
  public listaUf: any[] = [];
  public listaSituacao: any[] = [];
  public listaRegrasComissao: any[] = [];
  public listaTipoPessoa: any[] = [];
  public listaRegimeTributario: any[] = [];
  public listaTipoComissao: any[] = [];
  public listaTipoTributario: any[] = [];
  public listaIndicadorInscricaoEstadual: any[] = [];
  public listaUsuarios: any[] = [];

  // Scroll Spy
  public sections: ScrollSpySection[] = [
    { id: 'info-basica', label: 'Informações Básicas' },
    { id: 'endereco', label: 'Endereços e Contatos' },
    { id: 'comissao', label: 'Comissão' },
    { id: 'fiscal', label: 'Dados Fiscais' },
    { id: 'complementar', label: 'Dados Complementares' },
    { id: 'restricoes', label: 'Restrições de acesso' },
  ];

  // --- Hierarquia (US#147478) ---
  public EscopoVisualizacaoEnum = EscopoVisualizacaoEnum;
  // Escopo único do vendedor: vale para o CRM, o Processo de vendas e, no futuro, Nota e Financeiro.
  public readonly opcoesEscopo = [
    { id: EscopoVisualizacaoEnum.Tudo, rotulo: 'Ver tudo', descricao: 'Vê todos os registros.' },
    { id: EscopoVisualizacaoEnum.Proprios, rotulo: 'Ver apenas os próprios', descricao: 'Vê só os registros em que é o vendedor.' },
    { id: EscopoVisualizacaoEnum.Equipe, rotulo: 'Ver a equipe', descricao: 'Vê os seus e os da equipe, em qualquer nível.' },
  ];
  public flatHierarquia: VendedorHierarquiaStruct[] = [];
  public membrosIniciais: number[] = [];
  public idsEquipeAtual: number[] = [];
  // Restrições de acesso (Story, seção 4.1) só editável para "Ativo com acesso ao sistema".
  public restricoesDisponiveis = false;

  public get opcoesVendedorResponsavel(): { id: number; descricao: string }[] {
    // Inativos não entram como opção nova — exceto o valor já selecionado no formulário, pra não
    // sumir do campo se o vendedor responsável atual foi inativado depois de vinculado (Story: BUG 8).
    const valorAtual = this.idVendedorResponsavel.value;
    const ativoOuValorAtual = (v: VendedorHierarquiaStruct) =>
      v.idSituacaoVendedor !== SituacaoVendedorEnum.Inativo || v.idVendedor === valorAtual;

    if (this.idVendedor == null) {
      return this.flatHierarquia.filter(ativoOuValorAtual).map(v => ({ id: v.idVendedor, descricao: v.nome }));
    }
    const descendentes = VendedorHierarquiaHelper.getDescendentes(this.idVendedor, this.flatHierarquia);
    return this.flatHierarquia
      .filter(v => v.idVendedor !== this.idVendedor)
      .filter(v => !descendentes.has(v.idVendedor))
      .filter(v => !this.idsEquipeAtual.includes(v.idVendedor))
      .filter(ativoOuValorAtual)
      .map(v => ({ id: v.idVendedor, descricao: v.nome }));
  }

  public get temSubordinados(): boolean {
    return this.idsEquipeAtual.length > 0
      || this.idsEquipeAtual.some(id => VendedorHierarquiaHelper.getQtdSubordinadosDiretos(id, this.flatHierarquia) > 0);
  }

  public onEquipeChange(idsEquipe: number[]): void {
    this.idsEquipeAtual = idsEquipe;
  }

  // Controls state
  public showDiscountGrid: boolean = false;
  public preloadedCity: any[] = [];

  // Upload
  public files: File[] = [];
  public maxFileSize = 2 * 1024 * 1024; // 2MB
  public maxTotalSize = 10 * 1024 * 1024; // 10MB

  // Campos obrigatórios configuráveis (Configurações > Cadastros > Campos obrigatórios).
  // Se a configuração não carregar, o formulário mantém só as obrigatoriedades fixas.
  public camposObrigatorios: CampoObrigatorioStruct[] = [];
  private readonly validadorPercentualComissaoFixa = valorMaiorQueZeroValidator();
  private readonly validadorFaixasComissao = minArrayLengthValidator(1);
  // nome_tecnico (coluna no banco) -> controle do formulário que recebe Validators.required.
  private readonly controlePorCampoConfiguravel: Record<string, string> = {
    email: 'email',
    telefone: 'telefone',
    id_regra_liberacao_comissao: 'idRegraLiberacaoComissao',
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private vendedorService: VendedorService,
    private pessoaLookupService: PessoaLookupService,
    private alertService: AlertService,
    private utilService: UtilService,
    private selectPesquisavelService: SelectPesquisavelService,
    private consultaCnpjService: ConsultaCnpjService,
    private usuarioService: UsuarioService,
    private municipioService: MunicipioService,
    private unidadeFederativaService: UnidadeFederativaService,
    private motorFiscalLookupService: MotorFiscalLookupService,
    private maskService: MaskService,
    private pessoaConfiguracaoCampoObrigatorioService: PessoaConfiguracaoCampoObrigatorioService,
    private elementRef: ElementRef<HTMLElement>
  ) {
    this.percentMask = this.maskService.getPercentMask();
  }

  ngOnInit(): void {
    this.editor = new Editor();
    this.initForm();
    this.carregarCamposObrigatorios();
    this.carregarLookups();
    this.msgIdVendedor();
    this.listarUsuarios();
    this.carregarHierarquiaFlat();
  }

  // Story, seções 1-3: busca a lista plana da hierarquia uma vez ao carregar o cadastro; toda a
  // validação de ciclo/exclusividade do form roda sobre esse cache, sem round-trip por mudança.
  private carregarHierarquiaFlat(): void {
    this.vendedorService.getHierarquiaFlat().subscribe({
      next: res => {
        if (res.isError) return;
        this.flatHierarquia = res.lista;
        this.atualizarMembrosIniciais();
      }
    });
  }

  private atualizarMembrosIniciais(): void {
    if (this.idVendedor == null) {
      this.membrosIniciais = [];
    } else {
      this.membrosIniciais = this.flatHierarquia
        .filter(v => v.idVendedorResponsavel === this.idVendedor)
        .map(v => v.idVendedor);
    }
    this.idsEquipeAtual = [...this.membrosIniciais];
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  private initForm(): void {
    this.form = this.fb.group({
      // Seção 1: Básicas
      idTipoPessoa: [TipoPessoaEnum.Fisica, Validators.required], // 1=PF, 2=PJ, 3=Estrangeiro
      nome: ['', Validators.required],
      cpf: ['', cpfValidator()],
      cnpj: ['', cnpjValidator()],
      documentoEstrangeiro: [''],
      idPais: [null],
      idSituacaoVendedor: [null, Validators.required], // Default Ativo com acesso
      idUsuario: [null], // Se Ativo com acesso, validar se preenchido?

      // Seção 2: Endereço
      cep: [''],
      logradouro: [''],
      numero: [''],
      bairro: [''],
      idMunicipio: [{ value: null, disabled: true }],
      idUnidadeFederativa: [null],
      complemento: [''],
      email: ['', Validators.email],
      telefone: [''],
      emailComunicacao: ['', Validators.email],

      // Seção 3: Comissão
      idRegraLiberacaoComissao: [null],
      idTipoComissao: [null], // 1=Fixa, 2=Desconto
      percentualComissaoFixa: [null, [Validators.min(0)]],
      // Checkboxes controlados via idTipoComissao na UI, sem controls aqui

      // Seção 4: Fiscais
      idIndicadorInscricaoEstadual: [null],
      inscricaoEstadual: [''],
      inscricaoMunicipal: [''],
      idRegimeTributario: [null],
      idTipoTributario: [null],
      isConsumidorFinal: [false],
      isContribuinteIbsCbs: [false],
      inscricaoSuframa: [''],

      // Grid de Descontos
      faixasComissao: this.fb.array([]),

      // Seção 5: Complementares
      idVendedorResponsavel: [null],
      idSupervisor: [null],
      observacoesGerais: [''],

      // Seção 6: Restrições de acesso (Story, seção 4)
      idEscopoVendedor: [EscopoVisualizacaoEnum.Tudo],
    });

    // Listeners for dynamic validation
    this.form.get('idTipoPessoa')?.valueChanges.subscribe(val => this.onTipoPessoaChange(val));
    this.form.get('idTipoComissao')?.valueChanges.subscribe(val => this.onTipoComissaoChange(val));
    this.form.get('idIndicadorInscricaoEstadual')?.valueChanges.subscribe(val => this.onIndicadorInscricaoEstadualChange(val));
    this.form.get('idSituacaoVendedor')?.valueChanges.subscribe(val => this.onSituacaoVendedorChange(val));

    // Initialize Default State
    this.onTipoPessoaChange(TipoPessoaEnum.Fisica);
  }

  // Getters for Select Controls
  public get idPais(): FormControl {
    return this.form.get('idPais') as FormControl;
  }
  public get idSituacaoVendedor(): FormControl {
    return this.form.get('idSituacaoVendedor') as FormControl;
  }
  public get idUsuario(): FormControl {
    return this.form.get('idUsuario') as FormControl;
  }
  public get idRegraLiberacaoComissao(): FormControl {
    return this.form.get('idRegraLiberacaoComissao') as FormControl;
  }
  public get idTipoComissao(): FormControl {
    return this.form.get('idTipoComissao') as FormControl;
  }
  public get idIndicadorInscricaoEstadual(): FormControl {
    return this.form.get('idIndicadorInscricaoEstadual') as FormControl;
  }
  public get idRegimeTributario(): FormControl {
    return this.form.get('idRegimeTributario') as FormControl;
  }
  public get idTipoTributario(): FormControl {
    return this.form.get('idTipoTributario') as FormControl;
  }
  public get idVendedorResponsavel(): FormControl {
    return this.form.get('idVendedorResponsavel') as FormControl;
  }
  public get idSupervisor(): FormControl {
    return this.form.get('idSupervisor') as FormControl;
  }
  public get idMunicipio(): FormControl {
    return this.form.get('idMunicipio') as FormControl;
  } // For Online Select
  public get municipioSelecionado(): any {
    return this.preloadedCity[0] ?? null;
  }
  public get idUnidadeFederativa(): FormControl {
    return this.form.get('idUnidadeFederativa') as FormControl;
  } // For Offline Select
  public get idEscopoVendedor(): FormControl {
    return this.form.get('idEscopoVendedor') as FormControl;
  }

  public get rotuloEscopo(): string {
    return this.opcoesEscopo.find(o => o.id === this.idEscopoVendedor.value)?.rotulo ?? '';
  }

  public getNomeVendedor(idVendedor: number): string {
    return this.flatHierarquia.find(v => v.idVendedor === idVendedor)?.nome ?? '';
  }

  private msgIdVendedor(): void {
    const paramId = this.route.snapshot.paramMap.get('idVendedor');
    if (paramId) {
      this.idVendedor = Number(paramId);
      this.isEditMode = true;
      this.loadData(this.idVendedor);
    }
  }

  private loadData(id: number): void {
    this.isLoading = true;
    this.vendedorService.ObterPorId(id).subscribe({
      next: (res: VendedorResponse) => {
        this.isLoading = false;
        if (res.vendedor) {
          // Patch values
          this.form.patchValue(res.vendedor);
          // Handle special logic like checkboxes based on data
          if (res.vendedor.idTipoComissao == TipoComissaoEnum.Fixa) {
            this.form.patchValue({ useComissaoFixa: true });
          }
          if (res.vendedor.idTipoComissao == TipoComissaoEnum.Desconto) {
            this.form.patchValue({ useComissaoDesconto: true });
          }
          // Faixas sempre exibidas em ordem crescente de desconto, independentemente da ordem de cadastro
          [...res.faixasComissao]
            .sort((a, b) => (a.descontoAtePercentual ?? 0) - (b.descontoAtePercentual ?? 0))
            .forEach(x => {
              (this.form.controls['faixasComissao'] as FormArray).push(
                this.fb.group({
                  percentualDesconto: [x.descontoAtePercentual],
                  percentualComissao: [x.comissaoPercentual],
                })
              );
            });

          if (res.vendedor.idUnidadeFederativa && res.vendedor.idMunicipio) {
            this.atualizarEstadoMunicipio(res.vendedor.idUnidadeFederativa, false);
            this.municipioService.getByUf(res.vendedor.idUnidadeFederativa).subscribe(municipios => {
              const municipio = municipios.list.find(x => x.idMunicipio === res.vendedor!.idMunicipio);
              if (municipio) this.preloadedCity = [municipio];
            });
          }
          // Trigger change detections
          this.onTipoPessoaChange(res.vendedor.idTipoPessoa);
        }
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar o vendedor.', AlertType.error);
        this.router.navigate(['/cadastros/vendedores']);
      },
    });
  }

  private carregarLookups(): void {
    this.isLoading = true;
    this.pessoaLookupService.getLookup().subscribe({
      next: res => {
        if (!res.isError) {
          this.listaSituacao = res.listaSituacao;
          this.listaRegrasComissao = res.listaRegrasComissao;
          this.listaTipoPessoa = res.listaTipoPessoa;
          this.listaTipoComissao = res.listaTipoComissao;
          this.listaPaises = res.listaPais;
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao carregar listas.', AlertType.error);
      },
    });
    this.unidadeFederativaService.getUnidadesFederativas().subscribe(lista => {
      this.listaUf = lista.map(x => ({
        id: x.idUnidadeFederativa,
        descricao: x.descricaoExibicao,
        codigo: x.id,
      }));
    });
    this.motorFiscalLookupService.getIndicadoresInscricaoEstadual().subscribe(lista => this.listaIndicadorInscricaoEstadual = lista);
    this.motorFiscalLookupService.getRegimesTributarios().subscribe(lista => this.listaRegimeTributario = lista);
    this.motorFiscalLookupService.getTiposTributario().subscribe(lista => this.listaTipoTributario = lista);
  }

  // --- Logic Handlers ---

  onTipoPessoaChange(tipo: number | string): void {
    const tipoPessoa = Number(tipo);
    const cpfControl = this.form.get('cpf');
    const cnpjControl = this.form.get('cnpj');
    const docExtControl = this.form.get('documentoEstrangeiro');
    const paisControl = this.form.get('idPais');

    // Reset validations
    cpfControl?.clearValidators();
    cnpjControl?.clearValidators();
    docExtControl?.clearValidators();
    paisControl?.clearValidators();

    if (tipoPessoa === TipoPessoaEnum.Fisica) {
      // PF
      cpfControl?.enable();
      cpfControl?.setValidators([Validators.required, cpfValidator()]);
      cnpjControl?.disable();
      cnpjControl?.setValue(null);
      docExtControl?.disable();
      docExtControl?.setValue(null);
      paisControl?.disable();
    } else if (tipoPessoa === TipoPessoaEnum.Juridica) {
      // PJ
      cpfControl?.disable();
      cpfControl?.setValue(null);
      cnpjControl?.setValidators([Validators.required, cnpjValidator()]);
      cnpjControl?.enable();
      docExtControl?.disable();
      docExtControl?.setValue(null);
      paisControl?.disable();
    } else if (tipoPessoa === TipoPessoaEnum.Estrangeiro) {
      cpfControl?.setValue(null);
      // Estrangeiro
      cpfControl?.disable(); // Should remain visible? Prompt says: "Consistência de Visibilidade: Ao trocar para 'Estrangeiro', os campos CPF e CNPJ devem permanecer visíveis, mas com edição bloqueada."
      cnpjControl?.disable();
      cnpjControl?.setValue(null);
      docExtControl?.enable();
      paisControl?.enable();
      // Regex validation
      docExtControl?.setValidators([Validators.required, Validators.pattern(/[!-ÿ]{5,20}/)]);
      paisControl?.setValidators([Validators.required]);
    }

    cpfControl?.updateValueAndValidity();
    cnpjControl?.updateValueAndValidity();
    docExtControl?.updateValueAndValidity();
    paisControl?.updateValueAndValidity();
  }

  onSituacaoVendedorChange(situacao: number): void {
    const usuarioControl = this.form.get('idUsuario');

    if (situacao !== SituacaoVendedorEnum.Ativo) {
      usuarioControl?.setValue(null);
      usuarioControl?.disable();
    } else {
      usuarioControl?.enable();
    }

    // Story, seção 4.1: seção "Restrições de acesso" só editável para "Ativo com acesso ao sistema".
    this.restricoesDisponiveis = situacao === SituacaoVendedorEnum.Ativo;
    const escopoControl = this.form.get('idEscopoVendedor');
    if (this.restricoesDisponiveis) {
      escopoControl?.enable();
    } else {
      escopoControl?.setValue(EscopoVisualizacaoEnum.Tudo);
      escopoControl?.disable();
    }
  }

  onTipoComissaoChange(tipo: number): void {
    const percentualFixa = this.form.get('percentualComissaoFixa');

    if (tipo === TipoComissaoEnum.Fixa) {
      percentualFixa?.enable();
      this.showDiscountGrid = false;
    } else if (tipo === TipoComissaoEnum.Desconto) {
      percentualFixa?.disable();
      percentualFixa?.setValue(null);
      this.showDiscountGrid = true;
    } else {
      percentualFixa?.disable();
      percentualFixa?.setValue(null);
      this.showDiscountGrid = false;
    }

    this.aplicarObrigatoriedadeComissao();
  }

  setTipoComissao(tipo: TipoComissaoEnum, checked: boolean): void {
    const current = this.form.get('idTipoComissao')?.value;
    if (checked) {
      this.form.patchValue({ idTipoComissao: tipo });
    } else {
      // Se desmarcar o que está selecionado, limpa
      if (current === tipo) {
        this.form.patchValue({ idTipoComissao: null });
      }
    }
  }

  onIndicadorInscricaoEstadualChange(tipo: number): void {
    const inscEst = this.form.get('inscricaoEstadual');
    if (tipo === 1) {
      // 1 = Contribuinte ICMS
      inscEst?.enable();
      inscEst?.setValidators([Validators.required]);
    } else {
      inscEst?.clearValidators();
      // inscEst?.disable(); // Optional: disable if not needed
    }
    inscEst?.updateValueAndValidity();
  }

  private carregarCamposObrigatorios(): void {
    this.pessoaConfiguracaoCampoObrigatorioService.getByTela(TelaPessoaEnum.Vendedor).subscribe({
      next: res => {
        if (res.isError) return;
        this.camposObrigatorios = res.listaTelaComCampos[0]?.listaCampos ?? [];
        this.aplicarCamposObrigatorios();
      },
      // Falha na configuração não bloqueia o cadastro: seguem só as obrigatoriedades fixas.
      error: () => { },
    });
  }

  public isObrigatorio(nomeTecnico: string): boolean {
    return this.camposObrigatorios.some(c => c.nomeTecnico === nomeTecnico && c.isObrigatorio);
  }

  private aplicarCamposObrigatorios(): void {
    Object.entries(this.controlePorCampoConfiguravel).forEach(([nomeTecnico, controle]) =>
      this.alternarValidador(this.form.get(controle), Validators.required, this.isObrigatorio(nomeTecnico)));
    this.aplicarObrigatoriedadeComissao();
  }

  /**
   * Percentual de comissão obrigatório depende da modalidade marcada:
   * sem modalidade exige escolher uma; fixa exige Comissão (%) > 0; por desconto exige ao menos uma faixa.
   */
  private aplicarObrigatoriedadeComissao(): void {
    if (!this.form) return;
    const exigir = this.isObrigatorio('percentual_comissao');
    const tipo = this.form.get('idTipoComissao')?.value;
    this.alternarValidador(this.form.get('idTipoComissao'), Validators.required, exigir);
    this.alternarValidador(this.form.get('percentualComissaoFixa'), this.validadorPercentualComissaoFixa,
      exigir && tipo === TipoComissaoEnum.Fixa);
    this.alternarValidador(this.discountsArray, this.validadorFaixasComissao, exigir && tipo === TipoComissaoEnum.Desconto);
  }

  private alternarValidador(control: AbstractControl | null, validador: ValidatorFn, exigir: boolean): void {
    if (!control) return;
    if (exigir && !control.hasValidator(validador)) {
      control.addValidators(validador);
    } else if (!exigir && control.hasValidator(validador)) {
      control.removeValidators(validador);
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  /** Rola até o primeiro campo inválido e o foca. */
  private focarPrimeiroCampoInvalido(): void {
    setTimeout(() => {
      const campo = this.elementRef.nativeElement.querySelector<HTMLElement>(
        'input.ng-invalid, textarea.ng-invalid, mat-select.ng-invalid, .comissao-erro');
      campo?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      campo?.focus({ preventScroll: true });
    });
  }

  // --- Actions ---

  // Grid de Descontos Helpers
  get discountsArray(): FormArray {
    return this.form.get('faixasComissao') as FormArray;
  }

  createDescontoRow(): FormGroup {
    return this.fb.group({
      percentualDesconto: [null, Validators.required],
      percentualComissao: [null, Validators.required],
    });
  }

  addDescontoRow(): void {
    this.discountsArray.push(this.createDescontoRow());
    this.discountsArray.updateValueAndValidity();
  }

  removeDescontoRow(index: number): void {
    this.discountsArray.removeAt(index);
    this.discountsArray.updateValueAndValidity();
  }

  salvar(): void {
    console.log(this.form)
    const tipoPessoa = Number(this.form.get('idTipoPessoa')?.value);
    this.onTipoPessoaChange(tipoPessoa);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Há campos obrigatórios não preenchidos ou inválidos. Verifique os campos destacados.', AlertType.warning);
      this.focarPrimeiroCampoInvalido();
      return;
    }

    // Checks "Ativo com acesso" must have Usuario
    const situacao = this.form.get('idSituacaoVendedor')?.value;
    const usuario = this.form.get('idUsuario')?.value;
    if (situacao === SituacaoVendedorEnum.Ativo && !usuario) {
      this.alertService.show(
        'Atenção',
        'Para vendedor com acesso ao sistema, selecione um usuário.',
        AlertType.warning
      );
      return;
    }

    // Story, seções 1/11/12: ciclo/exclusividade validados sobre o estado atual do form antes de
    // salvar (a mesma regra é revalidada no servidor — isto é só feedback imediato ao usuário).
    const idVendedorResponsavelForm = this.form.get('idVendedorResponsavel')?.value ?? null;
    if (this.idVendedor != null && VendedorHierarquiaHelper.criaCiclo(this.idVendedor, idVendedorResponsavelForm, this.flatHierarquia)) {
      this.alertService.show('Atenção', 'O vendedor responsável escolhido não pode ficar abaixo deste cadastro na hierarquia.', AlertType.warning);
      return;
    }
    if (idVendedorResponsavelForm != null && this.idsEquipeAtual.includes(idVendedorResponsavelForm)) {
      this.alertService.show('Atenção', 'Um vendedor não pode ser Vendedor Responsável e integrante da Equipe ao mesmo tempo.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const request: VendedorRequest = this.form.getRawValue(); // getRawValue to include disabled fields if needed, or normal value
    request.idsEquipe = this.idsEquipeAtual;
    request.idTipoPessoa = tipoPessoa;
    request.cpf = tipoPessoa === TipoPessoaEnum.Fisica ? this.form.get('cpf')?.value ?? '' : '';
    request.cnpj = tipoPessoa === TipoPessoaEnum.Juridica ? this.form.get('cnpj')?.value ?? '' : '';
    request.documentoEstrangeiro = tipoPessoa === TipoPessoaEnum.Estrangeiro
      ? this.form.get('documentoEstrangeiro')?.value ?? ''
      : '';
    request.idTipoComissao = this.form.get('idTipoComissao')?.value;
    request.faixasComissao = [];
    this.form.get('faixasComissao')?.value.forEach((x: any) => {
      var faixaComissao = new VendedorComissaoFaixaStruct();

      faixaComissao.comissaoPercentual = x.percentualComissao;
      faixaComissao.descontoAtePercentual = x.percentualDesconto;

      request.faixasComissao.push(faixaComissao);
    });
    // Adjust request object as needed to match API

    if (this.isEditMode && this.idVendedor) {
      this.vendedorService.Alterar(this.idVendedor, request).subscribe({
        next: res => this.handleSuccess(res),
        error: err => this.handleError(err),
      });
    } else {
      this.vendedorService.Incluir(request).subscribe({
        next: res => this.handleSuccess(res),
        error: err => this.handleError(err),
      });
    }
  }

  private handleSuccess(res: ReturnStruct): void {
    this.isSaving = false;
    if (res.isError) {
      this.alertService.show('Erro', res.errorDescription, AlertType.error);
    } else {
      this.alertService.show('Sucesso', 'Vendedor salvo com sucesso.', AlertType.success);
      this.router.navigate(['/cadastros/vendedores']);
    }
  }

  private handleError(err: any): void {
    this.isSaving = false;
    this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
  }

  cancelar(): void {
    this.router.navigate(['/cadastros/vendedores']);
  }

  changeCidade(item: any) {
    console.log(item)
    if (item?.idUnidadeFederativa) {
      this.form.patchValue({ idUnidadeFederativa: item.idUnidadeFederativa });
    }
  }

  changeUnidadeFederativa(item: any): void {
    this.atualizarEstadoMunicipio(item?.id ?? null);
  }

  private atualizarEstadoMunicipio(idUf: number | null, limpar: boolean = true): void {
    const municipioControl = this.form.get('idMunicipio');

    if (limpar) {
      municipioControl?.setValue(null);
      this.preloadedCity = [];
    }

    if (idUf) municipioControl?.enable({ emitEvent: false });
    else municipioControl?.disable({ emitEvent: false });
  }

  buscarCep(): void {
    if (this.isLoadingCep) {
      return;
    }
    const cep = this.form.get('cep')?.value;
    if (!cep || cep.length < 8) {
      return;
    }

    this.isLoadingCep = true;
    this.utilService.getAddressByCEP(cep).subscribe({
      next: (res: CEPCorreios) => {
        this.isLoadingCep = false;
        if (!res || !res.cep) {
          this.alertService.show('Aviso', 'CEP não encontrado.', AlertType.warning);
          return;
        }

        // Find Pais (Brasil)
        const paisBrasil = this.listaPaises.find(p => p.descricao.toLowerCase() === 'brasil');

        // Find UF
        // ViaCEP returns Sigla (SP, RJ). listUf also uses Sigla in description per user.
        const ufEncontrada = this.listaUf.find(u => u.codigo === res.uf);

        this.form.patchValue({
          logradouro: res.logradouro,
          bairro: res.bairro,
          idUnidadeFederativa: ufEncontrada ? ufEncontrada.id : null,
          idPais: paisBrasil ? paisBrasil.id : null,
        });
        this.atualizarEstadoMunicipio(ufEncontrada?.id ?? null);

        // Busca Cidade pelo IBGE
        if (res.localidade) {
          this.selectPesquisavelService.getSelectValues<any>(this.urlMunicipio, res.localidade).subscribe({
            next: cidades => {
              const cidadeEncontrada = cidades.find(c => c.codIbge === res.ibge);
              if (cidadeEncontrada) {
                this.preloadedCity = [cidadeEncontrada];
                this.form.patchValue({ idMunicipio: cidadeEncontrada.idMunicipio });
              }
            },
          });
        }
      },
      error: err => {
        this.isLoadingCep = false;
        this.handleError(err);
      },
    });
  }

  public consultarCnpj(cnpj: string): void {
    if (!cnpj) {
      return;
    }
    if (this.isLoadingCnpj) {
      return;
    }
    this.isLoadingCnpj = true;
    this.consultaCnpjService.consultarCnpj(cnpj).subscribe({
      next: (res: ConsultaCnpjResponse) => {
        this.isLoadingCnpj = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }

        const dados = res.consultaCnpjStruct;

        // Find Pais (Brasil)
        const paisBrasil = this.listaPaises.find(p => p.descricao.toLowerCase() === 'brasil');

        // Find UF - Assuming response UF is "SP", "RJ", etc.
        const ufEncontrada = this.listaUf.find(u => u.codigo === dados.uf);

        this.form.patchValue({
          nome: dados.nome,
          // fantasia: dados.fantasia, // If needed and field exists
          cep: dados.cep?.replace(/\D/g, ''),
          logradouro: dados.logradouro,
          numero: dados.numero,
          complemento: dados.complemento,
          bairro: dados.bairro,
          email: dados.email,
          telefone: dados.telefone,
          idUnidadeFederativa: ufEncontrada ? ufEncontrada.id : null,
          idPais: paisBrasil ? paisBrasil.id : null,
          inscricaoEstadual: dados.inscricaoEstadual,
        });
        this.atualizarEstadoMunicipio(ufEncontrada?.id ?? null);

        // Find City by Name
        if (dados.municipio) {
          this.selectPesquisavelService.getSelectValues<any>(this.urlMunicipio, dados.municipio).subscribe({
            next: cidades => {
              // Try exact match first
              const cidadeEncontrada = cidades.find(c =>
                c.nome.toLowerCase().includes(dados.municipio.toLowerCase())
              );

              if (cidadeEncontrada) {
                this.preloadedCity = [cidadeEncontrada];
                this.form.patchValue({ idMunicipio: cidadeEncontrada.idMunicipio });
              }
            },
          });
        }
      },
      error: err => {
        this.isLoadingCnpj = false;
        this.alertService.show('Aviso', err, AlertType.warning);
      },
    });
  }

  private listarUsuarios(): void {
    this.usuarioService.getAll().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }

        this.listaUsuarios = res.listaUsuario;
      },
      error: err => {
        this.isLoading = false;
        this.handleError(err);
      },
    });
  }
}
